import { useEffect, useRef, useState } from "react";
import { streamRunEvents } from "./runApi";
import type { RunEventRecord } from "./runTypes";

const MAX_EVENTS = 300;

/** Replays from cursor 0, then follows the live tail; reconnects on `retry`/disconnect. */
export function useRunEvents(accountId: string, runId: string | null, terminal: boolean) {
  const [events, setEvents] = useState<RunEventRecord[]>([]);
  const cursorRef = useRef(0);
  const terminalRef = useRef(terminal);

  useEffect(() => {
    terminalRef.current = terminal;
  }, [terminal]);

  useEffect(() => {
    if (!runId) return;
    setEvents([]);
    cursorRef.current = 0;
    const abort = new AbortController();
    let stopped = false;

    const connect = async () => {
      while (!stopped) {
        try {
          await streamRunEvents({
            accountId,
            runId,
            cursor: cursorRef.current,
            signal: abort.signal,
            onEvent: (event) => {
              cursorRef.current = Math.max(cursorRef.current, event.seq);
              setEvents((previous) =>
                previous.some((candidate) => candidate.seq === event.seq)
                  ? previous
                  : [...previous, event].slice(-MAX_EVENTS),
              );
            },
            onRetry: (cursor) => {
              cursorRef.current = Math.max(cursorRef.current, cursor);
            },
          });
        } catch {
          if (stopped) return;
        }
        if (stopped || terminalRef.current) return;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    };

    void connect();
    return () => {
      stopped = true;
      abort.abort();
    };
  }, [accountId, runId]);

  return events;
}
